"use client";

import { cn } from "@jesus-central-church/shared";
import type { ElementType, JSX, Ref } from "react";
import React from "react";

const VARIANT_TO_COMPONENT: {
  readonly [key in
    | "display"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"]: keyof JSX.IntrinsicElements;
} = {
  display: "div",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
} as const;

const getHeadingVariants = (
  variant: "display" | "h1" | "h2" | "h3" | "h4" | "h5",
  weight?: "regular" | "medium" | "bold",
): string => {
  // Note: No margin included - spacing should be handled by parent container
  return cn(
    "font-heading tracking-tight",
    weight === "regular" && "font-regular",
    weight === "medium" && "font-medium",
    weight === "bold" && "font-bold",
    variant === "display" &&
      "text-4xl font-extrabold uppercase sm:text-5xl md:text-6xl lg:text-7xl",
    variant === "h1" && "text-3xl font-extrabold uppercase sm:text-4xl md:text-5xl",
    variant === "h2" && "text-2xl font-extrabold uppercase sm:text-3xl md:text-4xl",
    variant === "h3" && "text-xl font-extrabold uppercase sm:text-2xl md:text-3xl",
    variant === "h4" && "text-lg font-extrabold uppercase sm:text-xl md:text-2xl",
    variant === "h5" && "text-base font-extrabold uppercase sm:text-lg md:text-xl",
  );
};

interface HeadingProperties extends React.HTMLAttributes<HTMLHeadingElement> {
  variant: "display" | "h1" | "h2" | "h3" | "h4" | "h5";
  as?: ElementType;
  ref?: Ref<HTMLHeadingElement>;
  weight?: "regular" | "medium" | "bold";
  children?: React.ReactNode;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any; // For additional props like storyblokEditable
}

export const Heading = ({
  as,
  variant,
  className,
  weight,
  children,
  ...additionalProperties
}: HeadingProperties) => {
  // Determine component type outside of JSX to avoid creating during render
  const componentType: ElementType =
    as || (VARIANT_TO_COMPONENT[variant] as ElementType);

  const properties = {
    "data-variant": variant,
    className: cn(getHeadingVariants(variant, weight), className),
    ...additionalProperties,
    children,
  };

  // Use createElement to avoid ESLint "Cannot create components during render" error
  // This is the recommended pattern for polymorphic components in React
  return React.createElement(componentType, properties);
};
